import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import SessionExerciseSetsEditor from './SessionExerciseSetsEditor';
import {
  clampInt,
  getSetDisplaySeconds,
  getSetDisplayWeight,
  isHeightBasedExercise,
  isTimeBasedExercise,
  isWeightBasedExercise,
  syncExercisePrescribedCounts,
} from '../utils/sessionExercisePerformance';

function labelInitials(text, len = 2) {
  return String(text || '?').trim().substring(0, len).toUpperCase() || '?';
}

function formatExerciseSetsRepsSummary(exercise, weightInKg = true) {
  const synced = syncExercisePrescribedCounts(exercise);
  const setCount = synced.performance_sets?.length || synced.sets || 1;
  const parts = [`${setCount} sets`];
  if (isTimeBasedExercise(exercise)) {
    const firstSet = synced.performance_sets?.[0];
    const seconds = firstSet
      ? getSetDisplaySeconds(firstSet, exercise)
      : clampInt(exercise.time ?? synced.reps ?? 0, 0);
    parts.push(`${seconds}s`);
  } else {
    const repCounts = (synced.performance_sets || []).map((setRow) =>
      clampInt(setRow.prescribed_reps ?? synced.reps ?? 1)
    );
    const uniqueReps = [...new Set(repCounts)];
    const repsText =
      uniqueReps.length > 1
        ? `${Math.min(...uniqueReps)}–${Math.max(...uniqueReps)} reps`
        : `${uniqueReps[0] ?? synced.reps ?? 1} reps`;
    parts.push(repsText);
  }
  if (isHeightBasedExercise(exercise)) parts.push('height');
  if (isWeightBasedExercise(exercise)) {
    const firstSet = synced.performance_sets?.[0];
    const w = firstSet ? getSetDisplayWeight(firstSet, weightInKg) : null;
    if (w) parts.push(`${w} ${weightInKg ? 'kg' : 'lbs'}`);
  }
  return parts.join(' · ');
}

export default function RoutineExerciseTimeline({
  theme,
  exerciseTypes = [],
  expandedExerciseTypes,
  onToggleExerciseType,
  expandedExerciseIds,
  onToggleExerciseExpanded,
  editable = false,
  onRemoveExercise,
  studentId,
  weightInKg = true,
  emptyText = 'No exercises in this routine yet.',
}) {
  const styles = getStyles(theme);

  if (!exerciseTypes.length) {
    return <Text style={styles.emptyText}>{emptyText}</Text>;
  }

  return (
    <>
      {exerciseTypes.map((exerciseType, index) => {
        const typeKey = String(exerciseType.exercise_type_id);
        const isExpanded = expandedExerciseTypes.has(exerciseType.exercise_type_id);
        const typeName = exerciseType.exercise_type_name || 'Exercise';
        const exercises = Array.isArray(exerciseType.exercises) ? exerciseType.exercises : [];

        return (
          <View
            key={exerciseType.id || typeKey || index}
            style={styles.timelineItem}
            {...(Platform.OS === 'web' ? { dataSet: { sessionTimelineItem: 'true' } } : {})}
          >
            <View style={styles.exerciseTypeRow}>
              <TouchableOpacity
                style={styles.exerciseTypeContainer}
                onPress={() => onToggleExerciseType(exerciseType.exercise_type_id)}
                activeOpacity={0.7}
                {...(Platform.OS === 'web'
                  ? { dataSet: { sessionTimelineTypeCard: 'true' } }
                  : {})}
              >
                <View style={styles.exerciseTypeIconContainer}>
                  <View style={styles.exerciseTypeIcon}>
                    <Text style={styles.exerciseTypeIconText}>{labelInitials(typeName)}</Text>
                  </View>
                  <Text style={styles.exerciseTypeName}>{typeName}</Text>
                </View>
                <Text style={styles.expandArrow}>{isExpanded ? '▼' : '▶'}</Text>
              </TouchableOpacity>
            </View>

            {isExpanded ? (
              <View style={styles.exercisesDropdown}>
                {exercises.map((exercise, exIndex) => {
                  const isExerciseExpanded = expandedExerciseIds.has(exercise.id);
                  return (
                    <View
                      key={exercise.id || `${typeKey}-${exIndex}`}
                      style={styles.timelineExerciseItem}
                    >
                      <View style={styles.timelineExerciseHeader}>
                        <View style={styles.timelineExerciseIcon}>
                          <Text style={styles.timelineExerciseIconText}>
                            {labelInitials(exercise.exercise_name)}
                          </Text>
                        </View>
                        <View style={styles.timelineExerciseInfo}>
                          <Text style={styles.timelineExerciseName}>
                            {exercise.exercise_name || 'Exercise'}
                          </Text>
                          <Text style={styles.timelineExerciseDetails}>
                            {formatExerciseSetsRepsSummary(exercise, weightInKg)}
                          </Text>
                        </View>
                        <TouchableOpacity
                          style={styles.timelineExerciseExpandBtn}
                          onPress={() => onToggleExerciseExpanded(exercise.id)}
                          activeOpacity={0.7}
                          accessibilityRole="button"
                          accessibilityLabel={
                            isExerciseExpanded
                              ? `Collapse details for ${exercise.exercise_name}`
                              : `Expand details for ${exercise.exercise_name}`
                          }
                        >
                          <Text style={styles.timelineExerciseExpandArrow}>
                            {isExerciseExpanded ? '▼' : '▶'}
                          </Text>
                        </TouchableOpacity>
                        {editable ? (
                          <TouchableOpacity
                            style={styles.timelineExerciseRemoveBtn}
                            onPress={() => onRemoveExercise?.(exercise.id)}
                            activeOpacity={0.7}
                            accessibilityRole="button"
                            accessibilityLabel={`Remove ${exercise.exercise_name}`}
                          >
                            <MaterialIcons
                              name="close"
                              size={22}
                              color={theme.isDarkMode ? '#ffb4ab' : '#c62828'}
                            />
                          </TouchableOpacity>
                        ) : null}
                      </View>
                      {isExerciseExpanded ? (
                        <SessionExerciseSetsEditor
                          exercise={exercise}
                          theme={theme}
                          studentId={studentId}
                          weightInKg={weightInKg}
                          readOnly
                          onExerciseChange={() => {}}
                        />
                      ) : null}
                    </View>
                  );
                })}
              </View>
            ) : null}
          </View>
        );
      })}
    </>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    emptyText: {
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.8,
      lineHeight: 20,
      backgroundColor: 'transparent',
    },
    timelineItem: { marginBottom: 10, backgroundColor: 'transparent' },
    exerciseTypeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    exerciseTypeContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      flex: 1,
      padding: 10,
      backgroundColor: theme.isDarkMode ? theme.colors.background : theme.colors.surface,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    exerciseTypeIconContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      backgroundColor: 'transparent',
    },
    exerciseTypeIcon: {
      width: 50,
      height: 50,
      borderRadius: 25,
      backgroundColor: theme.colors.primary || '#d4af37',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 12,
    },
    exerciseTypeIconText: {
      color: '#FFFFFF',
      fontWeight: 'bold',
      fontSize: 14,
    },
    exerciseTypeName: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      flex: 1,
      backgroundColor: 'transparent',
    },
    expandArrow: {
      fontSize: 16,
      color: theme.colors.text,
      marginLeft: 10,
      backgroundColor: 'transparent',
    },
    exercisesDropdown: {
      marginTop: 5,
      marginLeft: 20,
      paddingLeft: 20,
      borderLeftWidth: 2,
      borderLeftColor: theme.colors.primary || '#d4af37',
      backgroundColor: 'transparent',
    },
    timelineExerciseItem: {
      padding: 8,
      marginBottom: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F0F0F0',
      borderRadius: 6,
      borderWidth: theme.isDarkMode ? 0 : 1,
      borderColor: theme.isDarkMode ? 'transparent' : '#E8E8E8',
    },
    timelineExerciseHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    timelineExerciseIcon: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: theme.colors.primary || '#d4af37',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 10,
    },
    timelineExerciseIconText: {
      color: '#FFFFFF',
      fontWeight: 'bold',
      fontSize: 12,
    },
    timelineExerciseInfo: { flex: 1, backgroundColor: 'transparent' },
    timelineExerciseName: {
      fontSize: 14,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    timelineExerciseDetails: {
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.7,
      marginTop: 2,
      backgroundColor: 'transparent',
    },
    timelineExerciseExpandBtn: {
      paddingHorizontal: 8,
      paddingVertical: 8,
      backgroundColor: 'transparent',
    },
    timelineExerciseExpandArrow: {
      fontSize: 14,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    timelineExerciseRemoveBtn: {
      paddingHorizontal: 6,
      paddingVertical: 6,
      backgroundColor: 'transparent',
    },
  });
